<script setup>
import { ref, computed } from 'vue';
import { getWireDiameters, getShieldingGasOptions, getTransferType } from './lib/data.js';
import { calculateDepositionRate, validateInputs } from './lib/logic.js';

// State
const weldProcess = ref('');
const wireDiameter = ref('');
const wireDensity = ref(null);
const shieldingGas = ref('');
const efficiency = ref(null);
const shieldingGasLabel = ref('');
const wireFeedSpeed = ref(400);
const hasCalculated = ref(false);

// Available options
const weldProcesses = [
  'GMAW - Solid Wire',
  'GMAW - Metal Core',
  'Flux Core - Gas-Shielded',
  'SAW - Solid Wire',
  'SAW - Cored Wire',
];

const wireDiameters = computed(() => {
  if (!weldProcess.value) return [];
  return getWireDiameters(weldProcess.value);
});

const shieldingGasOptions = computed(() => {
  if (!weldProcess.value) return [];
  return getShieldingGasOptions(weldProcess.value);
});

// Computed results
const transferType = computed(() => {
  if (!weldProcess.value || !shieldingGasLabel.value) return '';
  return getTransferType(weldProcess.value, shieldingGasLabel.value) || '';
});

const depositionRate = computed(() => {
  if (!hasCalculated.value) return 0;

  const speed = Number(wireFeedSpeed.value);
  if (!validateInputs(weldProcess.value, wireDensity.value, efficiency.value, speed)) {
    return 0;
  }

  return calculateDepositionRate(speed, wireDensity.value, efficiency.value);
});

const errorMessage = ref('');
const showResults = ref(false);

// Event handlers
function handleWeldProcessChange() {
  // Reset dependent fields
  wireDiameter.value = '';
  wireDensity.value = null;
  shieldingGas.value = '';
  shieldingGasLabel.value = '';
  efficiency.value = null;
  hasCalculated.value = false;
  showResults.value = false;
}

function handleWireDiameterChange(event) {
  const selectedValue = event.target.value;
  wireDensity.value = selectedValue ? Number(selectedValue) : null;
}

function handleShieldingGasChange(event) {
  const selectedOption = event.target.options[event.target.selectedIndex];
  efficiency.value = event.target.value ? Number(event.target.value) : null;
  shieldingGasLabel.value = selectedOption.text;
}

function calculate() {
  const speed = Number(wireFeedSpeed.value);

  if (!validateInputs(weldProcess.value, wireDensity.value, efficiency.value, speed)) {
    errorMessage.value = 'Please fill out all fields';
    showResults.value = false;
    return;
  }

  errorMessage.value = '';
  hasCalculated.value = true;
  showResults.value = true;
}
</script>

<template>
  <div class="hb-container" style="max-width: 340px; margin: 50px auto">
    <h2 class="hb-page-title">Deposition Calculator</h2>

    <ul class="weld-calculator">
      <li class="weld-calculator-twocol">
        <label for="weld-process">Weld Process:</label>
        <select
          id="weld-process"
          v-model="weldProcess"
          class="hb-select"
          @change="handleWeldProcessChange"
        >
          <option value="">--Select--</option>
          <option v-for="process in weldProcesses" :key="process" :value="process">
            {{ process }}
          </option>
        </select>
      </li>

      <li v-if="weldProcess" class="weld-calculator-twocol">
        <label for="wire-diameter">Wire Diameter:</label>
        <select
          id="wire-diameter"
          v-model="wireDiameter"
          class="hb-select"
          @change="handleWireDiameterChange"
        >
          <option value="">--Select--</option>
          <option v-for="option in wireDiameters" :key="option.label" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </li>

      <li v-if="weldProcess" class="weld-calculator-twocol">
        <label for="shielding-gas">Shielding Gas:</label>
        <select
          id="shielding-gas"
          v-model="shieldingGas"
          class="hb-select"
          @change="handleShieldingGasChange"
        >
          <option value="">--Select--</option>
          <option v-for="option in shieldingGasOptions" :key="option.label" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </li>

      <li v-if="weldProcess" class="weld-calculator-twocol">
        <label for="wire-feed-speed">
          Wire Feed Speed:<br />
          (inches per minute)
        </label>
        <input id="wire-feed-speed" v-model.number="wireFeedSpeed" type="number" class="hb-input" />
      </li>

      <li v-if="weldProcess">
        <button type="button" class="hb-button" @click="calculate">Calculate</button>
        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

        <div v-if="showResults" class="results-output">
          <div>Transfer Type = {{ transferType }}</div>
          <div>Efficiency = {{ efficiency }}%</div>
          <div>Deposition Rate = {{ depositionRate }} lbs/hour</div>
        </div>

        <div v-if="showResults" class="calculator-notes">
          • Tubular wires will require higher wire feed speeds to achieve equal amperage/heat input
          as solid wires of the same diameter.
          <br />
          • For complete parameter recommendations see product data sheets at
          <a href="https://www.hobartbrothers.com/products/" target="_blank" rel="noopener">
            www.hobartbrothers.com/products
          </a>
        </div>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.weld-calculator {
  font-size: 18px;
  list-style-type: none;
  margin: 20px auto;
  padding: 0;
}

.weld-calculator > li {
  margin-top: 20px;
}

.weld-calculator-twocol {
  display: flex;
  gap: 12px;
  align-items: center;
}

.weld-calculator-twocol > label {
  width: 45%;
  min-width: 45%;
}

.weld-calculator-twocol > select,
.weld-calculator-twocol > input {
  width: 55%;
  flex: 1;
}

.hb-select,
.hb-input {
  font-size: 18px;
}

.error-message {
  background-color: #ffc7c7;
  border: 1px solid #c3a2a2;
  margin-top: 20px;
  padding: 10px;
  text-align: center;
}

.results-output {
  border: 1px solid #ddd;
  padding: 15px;
  margin-top: 30px;
}

.results-output > div {
  margin-top: 10px;
}

.results-output > div:first-child {
  margin-top: 0;
}

.calculator-notes {
  font-size: 12px;
  margin-top: 20px;
}

.calculator-notes a {
  color: var(--hb-accent, #f37021);
}
</style>
